import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Map, TileLayer, ZoomControl } from 'react-leaflet';
import { healthsafety } from '@iso/services';
import { useSelector } from 'react-redux';
import AssetHealthSafety from './AssetHealthSafety';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import Control from '@skyeer/react-leaflet-custom-control'
import IntlMessages from '@iso/components/utility/intlMessages';
import { Button, Dropdown, Menu, Checkbox } from 'antd';
import { iotDevice } from '@iso/services';
import DeviceMapMarker from '@iso/containers/Pages/SafeFarm/DeviceMapMarker';
import { DownOutlined} from '@ant-design/icons';
import AppLocale from '@iso/config/translation';
import { IntlProvider } from 'react-intl';
import _ from 'lodash';
import L from 'leaflet';
import { propertyHelper } from "@iso/lib/helpers/propertyHelper";
import ActivityHazardMarkers from '@iso/containers/Pages/PropertyMap/components/Hazard/ActivityHazardMarkers';
import Hazard from '@iso/containers/Pages/PropertyMap/components/Hazard/Hazard';
import ModalActivity from '@iso/containers/Pages/HealthSafety/ModalActivity';
import ObservationMarkers from './ObservationMarkers';
import {
  normalizeHealthSafetyType,
  requiresLocation,
} from '@iso/constants/healthSafetyTypes';

const asArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getAttachmentIdentifier = (item) => {
  if (!item) return null;
  const raw =
    item.id ??
    item.attachment_id ??
    item.uid ??
    item.url ??
    item.full_path ??
    item.path ??
    item.file_path ??
    item.name;
  if (raw === undefined || raw === null) {
    return null;
  }
  return String(raw);
};

const simplifyAttachment = (raw) => {
  if (!raw) return null;

  if (typeof raw === 'string') {
    const name = raw.split('/').pop() || 'attachment';
    return {
      uid: raw,
      id: undefined,
      name,
      url: raw,
      full_path: raw,
      status: 'done',
    };
  }

  const base = raw.attachment ? { ...raw.attachment } : { ...raw };
  const identifier =
    base.id ??
    base.attachment_id ??
    base.uid ??
    base.url ??
    base.full_path ??
    base.path ??
    base.file_path;

  if (!identifier) {
    return null;
  }

  const url = base.url || base.full_path || base.path || base.file_path || undefined;
  const fullPath = base.full_path || base.path || base.file_path || url || undefined;
  const name =
    base.original_filename ||
    base.name ||
    (typeof fullPath === 'string' ? fullPath.split('/').pop() : undefined) ||
    'attachment';

  return {
    id: base.id ?? base.attachment_id ?? undefined,
    uid: String(identifier),
    name,
    url,
    full_path: fullPath,
    status: base.status || 'done',
  };
};

const collectAttachments = (activity, metadata) => {
  const sources = [
    metadata.attachments,
    metadata.attachment,
    metadata.attachment_activity,
    activity?.attachments,
    activity?.attachment,
    activity?.attachment_activity,
  ]
    .flatMap(asArray)
    .filter(Boolean);

  const seen = new Set();
  const result = [];

  sources.forEach((item) => {
    const normalized = simplifyAttachment(item);
    if (!normalized) return;
    if (seen.has(normalized.uid)) return;
    seen.add(normalized.uid);
    result.push(normalized);
  });

  return result;
};

const MapHealthSafety = (props) => {
  const mapRef = useRef();
  const localModalRef = useRef(false);
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const activePropertySubscription = useSelector((state) => state.subscription.activePropertySubscription);
  const { locale } = useSelector(state => state.LanguageSwitcher.language);
  const controlDraw = React.useRef(null);
  const [height, setHeight] = useState(null);
  const [trackTrace, setTrackTrace] = useState(false);
  const [loading, setLoading] = useState(false);
  const [positionDevice, setPositionDevice] = useState();
  const currentAppLocale = AppLocale[locale];
  const [centerPoint, setCenterPoint] = useState();
  const [assets, setAssets] = useState([]);
  const [assetLocations, setAssetLocations] = useState([]);
  const [hazardLocations, setHazardLocations] = useState([]);
  const [observationLocations, setObservationLocations] = useState([]);
  const [showMarkerAlert, setShowMarkerAlert] = useState(false);
  const [hazardActivityModalVisible, setHazardActivityModalVisible] = useState(false);
  const [hazardActivityEditingPolicy, setHazardActivityEditingPolicy] = useState(null);
  const [hazardActivityInitialPoint, setHazardActivityInitialPoint] = useState(null);
  const [hazardActivityRefreshKey, setHazardActivityRefreshKey] = useState(0);
  const usingExternalModal = typeof props.onShowActivityModal === 'function';

  useEffect(() => {
    if (usingExternalModal) {
      localModalRef.current = false;
    }
  }, [usingExternalModal]);

  const updateCenterPoint = useCallback((nextCenter) => {
    if (!nextCenter) return;
    setCenterPoint((prev) => {
      if (
        prev &&
        Math.abs(prev.lat - nextCenter.lat) < 1e-6 &&
        Math.abs(prev.lng - nextCenter.lng) < 1e-6
      ) {
        return prev;
      }
      return nextCenter;
    });
  }, []);

  const extractAssetLocations = useCallback((data) => {
    const collection = Array.isArray(data) ? data : Object.values(data || {});
    return collection.reduce((acc, asset) => {
      const normalizedType = normalizeHealthSafetyType(asset.type);
      const latSource = asset.lat ?? asset.latitude;
      const lngSource = asset.lng ?? asset.longitude;
      if (requiresLocation(normalizedType) && latSource !== undefined && lngSource !== undefined) {
        const lat = parseFloat(latSource);
        const lng = parseFloat(lngSource);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          acc.push([lat, lng]);
        }
      }
      return acc;
    }, []);
  }, []);

  const updateAssetLocations = useCallback((data) => {
    setAssetLocations(extractAssetLocations(data));
  }, [extractAssetLocations]);

  const handleHazardLocationsChange = useCallback((positions = []) => {
    setHazardLocations(
      positions
        .map((coords) => {
          if (!Array.isArray(coords) || coords.length < 2) {
            return null;
          }
          const lat = Number(coords[0]);
          const lng = Number(coords[1]);
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
          }
          return [lat, lng];
        })
        .filter(Boolean)
    );
  }, []);

  const handleObservationLocationsChange = useCallback((positions = []) => {
    setObservationLocations(
      positions
        .map((coords) => {
          if (!Array.isArray(coords) || coords.length < 2) {
            return null;
          }
          const lat = Number(coords[0]);
          const lng = Number(coords[1]);
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
          }
          return [lat, lng];
        })
        .filter(Boolean)
    );
  }, []);

  const mapLocations = useMemo(() => {
    const activityLocations = [...hazardLocations, ...observationLocations];
    if (activityLocations.length) {
      return activityLocations;
    }
    return assetLocations;
  }, [assetLocations, hazardLocations, observationLocations]);

  const getDataAssets = (propertyId) => {
    if (!propertyId) return;
    healthsafety.getHealthAndSafetyAssets(propertyId).then(res => {
      if(res.code === '0000'){
        const assetList = res.assets || [];
        setAssets(assetList);
        updateAssetLocations(assetList);
      }
    })
  };

  useEffect(() => {
    if(activePropertyId){
      getDataAssets(activePropertyId);
      setHeight(document.getElementById('health-safety-map').offsetHeight - 40);
      props.updatePropertyDetail(activePropertyId);
    }    
  }, [activePropertyId]);

  const menuShowOptions = () => (
    <Menu>
      {propertyHelper.checkIsGoldPackage(activePropertySubscription) &&
      <Menu.Item key="trackTrace" htmlFor="dark-theme">
        <Checkbox checked={trackTrace}  onChange={onChangeTrackTrace} id='track-trace'>
          <IntlMessages id="propertyMapPage.trackTrace" />
        </Checkbox>
      </Menu.Item>
      }
    </Menu>
  );

  const getPositionDevice = () => {
    if (!mapRef?.current) return;

    const { leafletElement: map } = mapRef.current;
    
    let locations = [];

    iotDevice.getDeviceLocation(activePropertyId).then((res) => {
      if (res.code === "0000") {
        setPositionDevice(res.locations);
        setLoading(false);

        if (res.locations && res.locations.length > 0) {
          _.forEach(res.locations, (data) => {
            if (data.latitude && data.longitude) {
              locations.push([data.latitude, data.longitude]);
            }
          });

          if (locations.length) {
            let bounds = new L.LatLngBounds(locations);
            let center = bounds.getCenter();
            updateCenterPoint(center);
            if (locations.length > 1) {
              map.fitBounds(bounds, { padding: [30, 30] });
            } else {
              map.setView(locations[0], 16);
            }
          }
          
        }
      }
    });
  };

  const onChangeTrackTrace = (e) => {
    setTrackTrace(e.target.checked); 
    if( (e.target.checked) && assets ){
      getPositionDevice();
    }else{
      updateAssetLocations(assets);
    }
    
  }

  const fallbackPositionArray = Array.isArray(props.position) && props.position[0] !== null && props.position[1] !== null
    ? props.position
    : null;
  const fallbackCenterPoint = fallbackPositionArray
    ? { lat: fallbackPositionArray[0], lng: fallbackPositionArray[1] }
    : null;
  const propertyCenterPoint = centerPoint || fallbackCenterPoint;

  useEffect(() => {
    if (trackTrace) {
      return;
    }
    const { current = {} } = mapRef;
    const map = current.leafletElement;
    if (!map) {
      return;
    }

    if (!mapLocations.length) {
      if (propertyCenterPoint) {
        const fallbackCenter = L.latLng(propertyCenterPoint.lat, propertyCenterPoint.lng);
        updateCenterPoint(fallbackCenter);
        map.setView(fallbackCenter, 15);
        map.invalidateSize();
      }
      return;
    }

    const bounds = new L.LatLngBounds(
      mapLocations.map(([lat, lng]) => L.latLng(lat, lng))
    );
    const nextCenter = bounds.getCenter();
    updateCenterPoint(nextCenter);
    if (mapLocations.length === 1) {
      map.setView(nextCenter, 16);
    } else {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 18 });
    }
    map.invalidateSize();
  }, [mapLocations, propertyCenterPoint, trackTrace, updateCenterPoint]);

  const openHazardActivityModal = useCallback(
    (policy = null, point = null) => {
      if (typeof props.onShowActivityModal === 'function') {
        props.onShowActivityModal(policy?.type || 'hazard', policy);
        return;
      }

      if (localModalRef.current) {
        setHazardActivityEditingPolicy(policy);
        setHazardActivityInitialPoint(point);
        setHazardActivityModalVisible(true);
        return;
      }

      localModalRef.current = true;
      setHazardActivityEditingPolicy(policy);
      setHazardActivityInitialPoint(point);
      setHazardActivityModalVisible(true);
    },
    [props.onShowActivityModal]
  );

  const closeHazardActivityModal = useCallback(() => {
    localModalRef.current = false;
    setHazardActivityModalVisible(false);
    setHazardActivityEditingPolicy(null);
    setHazardActivityInitialPoint(null);
  }, []);

  const refreshHazardActivities = useCallback(() => {
    setHazardActivityRefreshKey((key) => key + 1);
    if (typeof props.onHazardUpdated === 'function') {
      props.onHazardUpdated();
    }
  }, [props.onHazardUpdated]);

  useEffect(() => {
    if (!usingExternalModal) {
      return;
    }
    const handleGlobalActivityRefresh = () => {
      refreshHazardActivities();
    };
    window.addEventListener('healthSafety:activityRefresh', handleGlobalActivityRefresh);
    return () => {
      window.removeEventListener('healthSafety:activityRefresh', handleGlobalActivityRefresh);
    };
  }, [usingExternalModal, refreshHazardActivities]);

  const normalizePolicyForModal = useCallback(
    (hazard) => {
      if (!hazard) return null;

      let metadata = {};
      if (hazard.metadata) {
        if (typeof hazard.metadata === 'string') {
          try {
            metadata = JSON.parse(hazard.metadata);
          } catch (error) {
            metadata = {};
          }
        } else {
          metadata = hazard.metadata || {};
        }
      }

      const lat =
        toNumber(hazard.lat) ??
        toNumber(metadata?.point?.lat) ??
        toNumber(metadata?.point?.latitude);
      const lng =
        toNumber(hazard.lng) ??
        toNumber(metadata?.point?.lng) ??
        toNumber(metadata?.point?.longitude);

      if (lat === null || lng === null) {
        return null;
      }

      const attachments = collectAttachments(hazard, metadata);
      const attachmentIds = [
        metadata.attachment_activity_id,
        hazard.attachment_activity_id,
      ]
        .flatMap(asArray)
        .map((value) => (value === undefined || value === null ? null : Number(value)))
        .filter((value) => value !== null);

      metadata.point = { lat, lng };
      metadata.attachments = attachments;
      metadata.attachment = attachments;
      metadata.hazard_category = metadata.hazard_category || hazard.hazard_category;
      metadata.hazard_type = metadata.hazard_type || hazard.hazard_type;
      metadata.hazard_type_other =
        metadata.hazard_type_other || hazard.hazard_type_other;
      metadata.actions = metadata.actions || hazard.actions;
      metadata.comments = metadata.comments || hazard.comments;
      if (attachmentIds.length) {
        metadata.attachment_activity_id = attachmentIds;
      }

      return {
        id: hazard.policy_id || hazard.id,
        type: hazard.type || 'hazard',
        property_id: hazard.property_id || activePropertyId,
        metadata,
        lat,
        lng,
        attachments,
        attachment: attachments,
        attachment_activity_id:
          attachmentIds.length ? attachmentIds : hazard.attachment_activity_id,
        date_time: hazard.date_time || metadata.date_time || null,
      };
    },
    [activePropertyId]
  );

  const handleOpenHazardModal = useCallback((hazard, fallbackPoint = null) => {
    // Hazards from the hazards table arrive pre-built by Hazard.buildEditingPolicy
    // with `isNewHazard: true` and all top-level fields (description, status,
    // allocated_to, etc.) already populated. normalizePolicyForModal is designed
    // for legacy activity_policy records (metadata-driven) and would strip those
    // fields, blanking the Details textarea and other inputs on reopen. Forward
    // the rich object unchanged in that case.
    const policy = hazard?.isNewHazard ? hazard : normalizePolicyForModal(hazard);
    const initialPoint = fallbackPoint || (hazard && hazard.lat !== undefined && hazard.lng !== undefined
      ? { lat: hazard.lat, lng: hazard.lng }
      : null);
    openHazardActivityModal(policy, initialPoint);
  }, [normalizePolicyForModal, openHazardActivityModal]);

  const locationCenter = useMemo(() => {
    if (mapLocations.length) {
      const [lat, lng] = mapLocations[0];
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return [lat, lng];
      }
    }
    return null;
  }, [mapLocations]);

  const mapCenter =
    locationCenter ||
    (propertyCenterPoint
      ? [propertyCenterPoint.lat, propertyCenterPoint.lng]
      : null) ||
    fallbackPositionArray ||
    [0, 0];

  // Live Safety Map layer filter (driven by the tile's filter tabs). 'all'
  // shows every layer; a specific value shows only that layer.
  const activeLayer = props.activeLayer || 'all';
  const showLayer = (key) => activeLayer === 'all' || activeLayer === key;

  return(
    <Map ref={mapRef} center={mapCenter} doubleClickZoom={false}  zoom={15} zoomControl={true} style={{width:'100%', height:'100%'}}>

     <ReactLeafletGoogleLayer type={'hybrid'} useGoogMapsLoader={false}/>
      {showLayer('assets') && (
        <AssetHealthSafety
            propertyId={props.propertyId}
            controlDraw={controlDraw}
            primaryObjects={props.primaryObjects}
            updatePropertyDetail={props.updatePropertyDetail}
        />
      )}
      {showLayer('hazards') && (
        <Hazard
          propertyId={props.propertyId || activePropertyId}
          reloadKey={hazardActivityRefreshKey}
          controlDraw={controlDraw}
          onOpenActivityModal={handleOpenHazardModal}
        />
      )}
      {showLayer('hazards') && (
        <ActivityHazardMarkers
          propertyId={props.propertyId || activePropertyId}
          reloadKey={hazardActivityRefreshKey}
          onOpenActivityModal={handleOpenHazardModal}
          onLocationsChange={handleHazardLocationsChange}
        />
      )}
      {showLayer('observations') && (
        <ObservationMarkers
          propertyId={props.propertyId || activePropertyId}
          reloadKey={hazardActivityRefreshKey}
          onLocationsChange={handleObservationLocationsChange}
        />
      )}
      <Control position="topright">
        <IntlProvider
            locale={currentAppLocale.locale}
            messages={currentAppLocale.messages}
        > 
          <Dropdown overlay={menuShowOptions} trigger={['click']}>
            <Button className="action-button">              
              <IntlMessages id="propertyMap.title.view" /> <DownOutlined />
            </Button>
          </Dropdown>
        </IntlProvider>
      </Control>
      {trackTrace &&
        <DeviceMapMarker 
          propertyId={activePropertyId} 
          getPositionDevice={getPositionDevice} 
          setLoading={setLoading} 
          loading={loading} 
          setShowMarkerAlert={setShowMarkerAlert}
          showMarkerAlert={showMarkerAlert}
          positionDevice={positionDevice} />
      }

      {!usingExternalModal && (
        <ModalActivity
          cancelModalActivity={closeHazardActivityModal}
          visibleModalActivity={hazardActivityModalVisible}
          propertyId={props.propertyId || activePropertyId}
          activityActive={hazardActivityEditingPolicy?.type || 'hazard'}
          searchAssetAndPolicy={refreshHazardActivities}
          editingPolicy={hazardActivityEditingPolicy}
          propertyCenter={hazardActivityInitialPoint || propertyCenterPoint}
          initialPoint={hazardActivityInitialPoint}
          // Route saves to the hazards table when the modal was opened from a
          // hazards-table marker (Hazard component sets isNewHazard:true via
          // buildEditingPolicy). Legacy activity_policy clicks from
          // ActivityHazardMarkers stay on the original save path.
          saveToHazardsTable={hazardActivityEditingPolicy?.isNewHazard === true}
        />
      )}
 
    </Map>
  )

}
export default MapHealthSafety;
